import React, {useEffect, useMemo, useRef, useState} from 'react';
import {ChevronDown, X} from 'lucide-react';

interface SearchableSelectProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabledPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  /** Show a clear (×) button when a value is selected. Defaults to true. */
  clearable?: boolean;
}

/**
 * Lightweight, dependency-free searchable dropdown (combobox).
 * Type to filter options; click to select. RTL-friendly.
 * Used for long option lists (e.g. KSA cities/districts) where a plain
 * <select> would force a long scroll.
 */
export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  options,
  onChange,
  placeholder = 'اختر',
  disabledPlaceholder,
  disabled = false,
  className = '',
  clearable = true
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) {return;}
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {return options;}
    return options.filter(o => o.toLowerCase().includes(q));
  }, [options, query]);

  const handleSelect = (option: string) => {
    onChange(option);
    setOpen(false);
    setQuery('');
  };

  const shownPlaceholder = disabled ? (disabledPlaceholder ?? placeholder) : placeholder;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 border border-gray-300 rounded-md px-3 py-2 bg-white text-right focus:ring-2 focus:ring-primary/30 focus:border-brand-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>
          {value || shownPlaceholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {clearable && value && !disabled && (
            <X
              className="w-4 h-4 text-gray-400 hover:text-gray-600"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
            />
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="بحث..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <ul className="overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">لا توجد نتائج</li>
            ) : (
              filtered.map(option => (
                <li key={option}>
                  <button
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={`w-full text-right px-3 py-2 text-sm hover:bg-gray-50 ${option === value ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-700'}`}
                  >
                    {option}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
